import React, { useMemo } from 'react';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import SettingsIcon from '@material-ui/icons/Settings';
import { useHistory, useLocation } from 'react-router-dom';
import { HorizontalTabPage, TabDefinition } from '../v2/layouts/HorizontalTabPage';
import CustomFieldLookUpListsTab from './tabs/customFieldLookUpLists/CustomFieldLookUpListsTab';
import CustomFieldGroupsTab from './tabs/customFieldGroups/CustomFieldGroupsTab';
import CustomFieldLookUpListSelectedTab from './tabs/customFieldLookUpLists/CustomFieldLookUpListSelectedTab';
import CustomFieldGroupSelectedTab from './tabs/customFieldGroups/CustomFieldGroupSelectedTab';
import { Box, Typography, makeStyles, Theme, createStyles } from '@material-ui/core';
import { EmailTemplateContentWithSidebar } from '../emailTemplatesConfiguration/EmailTemplateContentWithSidebar';
import { EmailTemplateMessageTypeEnum, EmailTemplateTypeEnum } from '../emailTemplatesConfiguration/EmailTemplateRespository';
import MailOutlineOutlinedIcon from '@material-ui/icons/MailOutlineOutlined';
import { EmailTemplateContent } from '../emailTemplatesConfiguration/EmailTemplateContent';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        // Matches PageContentLayout's controlPanel bar (Enquiry Summary's search/checkbox
        // toolbar) so this description bar looks consistent with the rest of the app.
        descriptionBar: {
            padding: theme.spacing(1, 2.5),
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 2px 4px rgba(0,0,0,0.07)',
        },
        descriptionText: {
            fontSize: 13,
            color: theme.palette.text.secondary,
        },
    })
);

enum EnquiryCustomFieldsList {
    CustomFieldGroups = 'CustomFieldGroups',
    CustomFieldLookUpLists = 'CustomFieldLookUpLists',
    DataCollectionForms = 'DataCollectionForms',
    DataCollectionCompletion = 'DataCollectionCompletion',
}

interface EnquiryCustomFieldsPageProps {

}

export default function EnquiryCustomFieldsPage(props: EnquiryCustomFieldsPageProps) {

    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();

    const searchParams = new URLSearchParams(location.search);
    const tabSelected = searchParams.get('tab');
    const tabKey = searchParams.get('tabKey');

    const selectedTab = Object.values(EnquiryCustomFieldsList).includes(tabSelected as EnquiryCustomFieldsList)
        ? (tabSelected as EnquiryCustomFieldsList)
        : EnquiryCustomFieldsList.CustomFieldGroups;

    const activeTab = selectedTab === EnquiryCustomFieldsList.CustomFieldGroups
        ? 0
        : selectedTab === EnquiryCustomFieldsList.CustomFieldLookUpLists
            ? 1
            : selectedTab === EnquiryCustomFieldsList.DataCollectionCompletion
                ? 2
                : 3;

    const tabs: TabDefinition[] = useMemo(() => ([
        {
            label: 'Custom Forms',
            icon: <SettingsIcon />,
            content: (
                <Box
                    display="flex" 
                    flexDirection="column"
                    flex={1}
                    height="100%"
                >
                    {!tabKey && (
                        <Box className={classes.descriptionBar}>
                            <Typography className={classes.descriptionText}>
                                User defined custom forms
                            </Typography>
                        </Box>
                    )}
                    <Box padding="20px">
                        {tabKey ? <CustomFieldGroupSelectedTab /> : <CustomFieldGroupsTab />}
                    </Box>
                </Box>
            )
        },
        {
            label: 'Custom Field Look Up Lists',
            icon: <SettingsIcon />,
            content: (
                <Box
                    display="flex" 
                    flexDirection="column"
                    flex={1}
                    height="100%"
                >
                    {!tabKey && (
                        <Box className={classes.descriptionBar}>
                            <Typography className={classes.descriptionText}>
                                Look up lists used for the validation of custom fields
                            </Typography>
                        </Box>
                    )}
                    <Box padding="20px">
                        {tabKey ? <CustomFieldLookUpListSelectedTab /> : <CustomFieldLookUpListsTab />}
                    </Box>
                </Box>
            )
        },
        {
            label: 'Data Collection Emails',
            icon: <MailOutlineOutlinedIcon />,
            content: (
                <Box
                    display="flex" 
                    flexDirection="column"
                    flex={1}
                    height="100%"
                >
                    {!tabKey && (
                        <Box className={classes.descriptionBar}>
                            <Typography className={classes.descriptionText}>
                                Provide email templates for data collection forms sent to clients.
                            </Typography>
                        </Box>
                    )}
                    <EmailTemplateContentWithSidebar 
                        emailType={EmailTemplateTypeEnum.EnquiryDataCollection}
                        messageType={EmailTemplateMessageTypeEnum.EMAIL}
                        showFields={{
                            name: true,
                            areaOfLaw: true,
                        }}
                    />
                </Box>
            )
        },
        {
            label: 'Form Completion Notification',
            icon: <MailOutlineOutlinedIcon />,
            content: (
                <Box
                    display="flex" 
                    flexDirection="column"
                    flex={1}
                    height="100%"
                >
                    {!tabKey && (
                        <Box className={classes.descriptionBar}>
                            <Typography className={classes.descriptionText}>
                                Provide email templates for completion of data collection forms sent to clients.
                            </Typography>
                        </Box>
                    )}
                    <EmailTemplateContent 
                        emailType={EmailTemplateTypeEnum.EnquiryDataCollectionCompletion}
                    />
                </Box>
            )
        },
    ]), [tabKey]);

    const onTabChange = (tabIndex: number) => {
        const tab = tabIndex === 0
            ? EnquiryCustomFieldsList.CustomFieldGroups
            : tabIndex === 1
                ? EnquiryCustomFieldsList.CustomFieldLookUpLists
                : tabIndex === 2
                    ? EnquiryCustomFieldsList.DataCollectionCompletion
                    : EnquiryCustomFieldsList.DataCollectionForms;

        history.push({
                pathname: 'enquiryCustomFieldsConfiguration',
                search: `?tab=${tab}`,
        });
    };

    const onBack = () => {
        history.push('/configurationDashboard');
    };

  return (
    <Box
        height="100%"
        display="flex"
        flexDirection="column"
        className="enquiry-custom-fields-page"
    >
        <HorizontalTabPage
            icon={<TextFieldsIcon />}
            title="Enquiry Electronic Intake Forms"
            subtitle="Define and maintain custom intake forms for the electronic collection of enquiry data."
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={onTabChange}
            onBack={onBack}
        />
    </Box>
  );
}